import React, { FC, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Box, Typography } from "@mui/material";

import { ALARMS } from "../../../../utils";
import { TPointsTooltip } from "../../utils";

export interface IStyledTooltip {
  data: TPointsTooltip | string;
}

const containerStyles = (isOverflowing: boolean) => ({
  position: "relative",
  maxHeight: "40vh",
  overflow: "hidden",
  ...(isOverflowing && {
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      height: "5em",
      background: "linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      fontWeight: "bold",
    },
  }),
});

const textStyle = {
  fontSize: 12,
  pl: 0.5,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const StyledTooltip: FC<IStyledTooltip> = ({ data }) => {
  const { formatMessage } = useIntl();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      setIsOverflowing(container.scrollHeight > container.clientHeight);
    }
  }, [data]);

  const isNoRecords = typeof data !== "string" && Object.values(data).every(points => points.length === 0);

  return (
    <>
      {typeof data === "string" ? (
        <Box ref={containerRef} sx={containerStyles(isOverflowing)}>
          {data.split("\n").map((line, index) => (
            <Box key={index} sx={{ mb: 0.5, width: 300, px: 1 }}>
              <Typography sx={textStyle}>{line}</Typography>
            </Box>
          ))}
        </Box>
      ) : isNoRecords ? (
        <Typography>{formatMessage({ id: "pages.journals.measures.noRecords" })}</Typography>
      ) : (
        <Box ref={containerRef} sx={containerStyles(isOverflowing)}>
          {Object.entries(data).map(([key, points]) => {
            if (!points.length) {
              return null;
            }

            return (
              <Box key={key} sx={{ mb: 0.5, width: 300, px: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: "bold" }}>{ALARMS[Number(key)]}:</Typography>
                </Box>
                <Box>
                  {points.map((point, index) => (
                    <Box key={`${point}-${index}`}>
                      <Typography sx={textStyle}>{point}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};
